import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { events as evtApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import {
  Users, Ticket, Loader2, ArrowLeft, CheckCircle2, ScanLine, Search, RefreshCw, Camera,
} from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/browser';

export default function EventHostDashboard() {
  const { eid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('list');   // list | scan
  const [lastCheckin, setLastCheckin] = useState(null);

  const load = async () => {
    try { setData(await evtApi.hostAttendees(eid)); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || 'Failed to load attendees.'); setData({ total: 0, checkedIn: 0, tickets: [] }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eid]);

  const checkIn = async (ticketId) => {
    if (!ticketId?.trim()) { toast({ title: 'Enter a ticket ID or scan the QR' }); return null; }
    setBusy(true);
    try {
      const r = await evtApi.hostCheckin(eid, ticketId.trim());
      const att = r.attendee || {};
      const primary = att.attendeeName || att.name || '';
      const sub = att.buyerName && att.buyerName !== primary ? `Bought by ${att.buyerName}` : (att.email || '');
      if (r.already) toast({ title: `Already checked in — ${primary}`, description: `${sub} · ${new Date(r.checkedInAt).toLocaleTimeString()}` });
      else            toast({ title: `Checked in ✓ ${primary}`, description: sub });
      setScanInput('');
      setLastCheckin(r);
      await load();
      return r;
    } catch (e) {
      toast({ title: 'Check-in failed', description: e?.response?.data?.detail || 'Try again' });
      return null;
    } finally { setBusy(false); }
  };

  if (err && !data) return (
    <div className="max-w-lg mx-auto pbb-card p-10 text-center" data-testid="host-error">
      <div className="font-semibold text-white">{err}</div>
      <Link to="/events" className="text-green-400 text-sm mt-3 inline-flex items-center gap-1"><ArrowLeft size={12}/> Events</Link>
    </div>
  );
  if (!data) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading attendees…</div>;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? data.tickets.filter(t => (t.ticketId || '').toLowerCase().includes(q)
                            || (t.attendee?.name || '').toLowerCase().includes(q)
                            || (t.attendee?.attendeeName || '').toLowerCase().includes(q)
                            || (t.attendee?.buyerName || '').toLowerCase().includes(q)
                            || (t.attendee?.email || '').toLowerCase().includes(q))
    : data.tickets;

  return (
    <div className="space-y-6" data-testid="host-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/events" className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white mb-1"><ArrowLeft size={12}/> Events</Link>
          <h1 className="text-2xl font-bold text-white">Attendee dashboard</h1>
          <p className="text-gray-400 text-sm">Scan tickets at the door or check people in by ID.</p>
        </div>
        <button onClick={load} className="text-xs px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-500 text-gray-300 inline-flex items-center gap-1">
          <RefreshCw size={12}/> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Registered" value={data.total} icon={Users}/>
        <Stat label="Checked in" value={data.checkedIn} icon={CheckCircle2} accent="text-green-400"/>
        <Stat label="Remaining" value={Math.max(0, data.total - data.checkedIn)} icon={Ticket} accent="text-yellow-300"/>
        <Stat label="Waitlist" value={data.waitlistSize || 0} icon={Users} accent="text-purple-300"/>
      </div>

      <div className="flex items-center gap-1 border-b border-[#1f1f1f] overflow-x-auto">
        <TabBtn active={tab === 'list'} onClick={() => setTab('list')} testId="host-tab-list" icon={ScanLine} label="Check-in list"/>
        <TabBtn active={tab === 'scan'} onClick={() => setTab('scan')} testId="host-tab-scan" icon={Camera} label="Scan QR"/>
      </div>

      {tab === 'scan' ? (
        <ScanTab onScan={checkIn} lastCheckin={lastCheckin}/>
      ) : (
        <>
          <div className="pbb-card p-5 space-y-3">
            <div className="text-sm font-semibold text-white flex items-center gap-2"><ScanLine size={14}/> Check someone in manually</div>
            <div className="flex gap-2 flex-wrap">
              <input value={scanInput} onChange={e => setScanInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') checkIn(scanInput); }}
                placeholder="Paste QR link or type ticket ID (e.g. 7C490A0FE4)"
                data-testid="host-checkin-input"
                className="flex-1 min-w-[220px] pbb-input"/>
              <button onClick={() => checkIn(scanInput)} disabled={busy}
                data-testid="host-checkin-submit"
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                Check in
              </button>
            </div>
            <div className="text-[11px] text-gray-500">
              Tip: switch to the <b>Scan QR</b> tab to use your camera instead.
            </div>
          </div>

          <div className="pbb-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Search size={14} className="text-gray-500"/>
              <input value={query} onChange={e => setQuery(e.target.value)}
                placeholder="Filter by name, email, or ticket ID"
                data-testid="host-attendee-filter"
                className="flex-1 pbb-input"/>
            </div>
            {filtered.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500" data-testid="host-attendees-empty">
                {q ? 'No attendees match that filter.' : 'No tickets sold yet.'}
              </div>
            ) : (
              <ul className="divide-y divide-[#1f1f1f]" data-testid="host-attendees-list">
                {filtered.map(t => {
                  const paid = t.purchaseType === 'paid';
                  const checked = !!t.checkedInAt;
                  const cancelled = t.status === 'cancelled' || !!t.cancelledAt || t.status === 'resold';
                  const att = t.attendee || {};
                  const primary = att.attendeeName || att.name || t.attendeeName || t.userName || 'Attendee';
                  const buyer = att.buyerName || t.userName || '';
                  const showBuyer = buyer && buyer.trim().toLowerCase() !== (primary || '').trim().toLowerCase();
                  return (
                    <li key={t.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`host-row-${t.ticketId}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white truncate" data-testid={`host-row-attendee-${t.ticketId}`}>{primary}</div>
                        {showBuyer && (
                          <div className="text-[11px] text-yellow-300 truncate" data-testid={`host-row-buyer-${t.ticketId}`}>
                            Bought by {buyer}
                          </div>
                        )}
                        <div className="text-xs text-gray-400 truncate">{att.email || t.userEmail}{att.phone ? ` · ${att.phone}` : ''}</div>
                        <div className="text-[11px] text-gray-500 font-mono tracking-widest mt-0.5">#{t.ticketId}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {cancelled
                          ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">VOID</span>
                          : checked
                            ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/40 inline-flex items-center gap-1"><CheckCircle2 size={10}/> CHECKED IN</span>
                            : <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40">READY</span>}
                        <div className={`text-[10px] px-1.5 py-0.5 rounded ${paid ? 'bg-yellow-500/20 text-yellow-300' : 'bg-gray-500/20 text-gray-300'}`}>
                          {paid ? `PAID · $${(t.priceUsd || 0).toFixed(2)}` : 'FREE'}
                        </div>
                      </div>
                      {!checked && !cancelled && (
                        <button onClick={() => checkIn(t.ticketId)} disabled={busy}
                          data-testid={`host-checkin-btn-${t.ticketId}`}
                          className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-xs inline-flex items-center gap-1 disabled:opacity-60">
                          <CheckCircle2 size={12}/> Check in
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, testId, icon: Icon, label }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap
        ${active ? 'border-green-500 text-white font-semibold' : 'border-transparent text-gray-400 hover:text-white'}`}>
      <Icon size={14}/> {label}
    </button>
  );
}

function Stat({ label, value, icon: Icon, accent = 'text-white' }) {
  return (
    <div className="pbb-card p-4">
      <div className="text-xs text-gray-400 flex items-center gap-1"><Icon size={12}/> {label}</div>
      <div className={`text-2xl font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function ScanTab({ onScan, lastCheckin }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState('');
  const [lastAt, setLastAt] = useState(0);

  const start = async () => {
    setError('');
    try {
      readerRef.current = new BrowserMultiFormatReader();
      // decodeFromVideoDevice returns controls we can stop later.
      const controls = await readerRef.current.decodeFromVideoDevice(
        undefined, videoRef.current,
        async (result, _err) => {
          if (!result) return;
          const text = result.getText();
          // Debounce — same QR within 3s is ignored.
          const now = Date.now();
          if (text === lastResult && now - lastAt < 3000) return;
          setLastResult(text); setLastAt(now);
          await onScan(text);
        },
      );
      controlsRef.current = controls;
      setActive(true);
    } catch (e) {
      setError('Could not access the camera. Please grant camera permission and try again.');
    }
  };

  const stop = () => {
    try { controlsRef.current?.stop(); } catch {}
    controlsRef.current = null;
    setActive(false);
  };

  useEffect(() => () => stop(), []);   // cleanup on unmount

  return (
    <div className="pbb-card p-5 space-y-3" data-testid="host-scan-panel">
      <div className="text-sm font-semibold text-white flex items-center gap-2"><Camera size={14}/> Scan ticket QR</div>
      <div className="relative aspect-video max-w-md mx-auto rounded-xl overflow-hidden bg-black border border-[#2a2a2a]">
        <video ref={videoRef} muted playsInline className="w-full h-full object-cover"/>
        {!active && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2">
            <Camera size={28}/> <div className="text-xs">Camera is off</div>
          </div>
        )}
        {active && (
          <>
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-3/5 aspect-square border-2 border-green-400 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"/>
            </div>
            <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-green-500 text-black font-bold px-2 py-0.5 rounded">Scanning…</div>
          </>
        )}
      </div>
      {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2" data-testid="host-scan-error">{error}</div>}
      <div className="flex justify-center gap-2">
        {!active ? (
          <button onClick={start} data-testid="host-scan-start"
            className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm inline-flex items-center gap-2">
            <Camera size={14}/> Start camera
          </button>
        ) : (
          <button onClick={stop} data-testid="host-scan-stop"
            className="px-4 py-2 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm inline-flex items-center gap-2">
            <Camera size={14}/> Stop camera
          </button>
        )}
      </div>
      {lastResult && (
        <div className="text-[11px] text-center text-gray-400" data-testid="host-scan-last">
          Last scanned: <span className="font-mono text-gray-200">{lastResult.slice(-14)}</span>
        </div>
      )}
      {lastCheckin && (() => {
        const att = lastCheckin.attendee || {};
        const primary = att.attendeeName || att.name || '';
        const buyer = att.buyerName || '';
        const showBuyer = buyer && buyer.trim().toLowerCase() !== (primary || '').trim().toLowerCase();
        return (
          <div className="mt-3 rounded-xl border border-green-500/40 bg-green-500/5 p-4" data-testid="host-scan-lastcheckin">
            <div className="text-[10px] tracking-widest text-green-300 uppercase inline-flex items-center gap-1">
              <CheckCircle2 size={11}/> {lastCheckin.already ? 'Already checked in' : 'Just checked in'}
            </div>
            <div className="text-xl font-bold text-white mt-1" data-testid="host-scan-attendee-name">{primary || 'Attendee'}</div>
            {showBuyer && (
              <div className="text-xs text-yellow-300 mt-0.5" data-testid="host-scan-buyer-name">Bought by {buyer}</div>
            )}
            <div className="text-xs text-gray-300 mt-1 space-y-0.5">
              {att.email && <div data-testid="host-scan-buyer-email">{att.email}</div>}
              {att.phone && <div data-testid="host-scan-buyer-phone">{att.phone}</div>}
              <div className="font-mono text-[11px] text-gray-500 tracking-widest">#{lastCheckin.ticket?.ticketId}</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
